/* ============================================================
   IDEAS CAFE — Hero slider

   Cross-fade between stacked slides. Navigation is the dash rail
   and the thumbnail rail; both are real buttons, so keyboard and
   assistive tech get the slider for free.

   Autoplay is suspended on hover, on focus-within, while the tab
   is hidden, and entirely under prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('ideas-hero')) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class IdeasHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      if (this.slides.length < 2) return;

      this.dashes = Array.prototype.slice.call(this.querySelectorAll('.ic-dash[data-goto]'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('.ideas-hero__thumb[data-goto]'));
      this.counter = this.querySelector('[data-current]');
      this.live = this.querySelector('[data-live-region]');

      this.index = 0;
      this.timer = null;
      this.interval = parseInt(this.dataset.interval, 10) || 7000;
      this.wantsAutoplay = this.dataset.autoplay === 'true';

      this._bindControls();
      this._bindPause();
      this._bindSwipe();
      this._bindKeys();

      this._start();
    }

    disconnectedCallback() {
      this._stop();
      if (this._onVisibility) {
        document.removeEventListener('visibilitychange', this._onVisibility);
      }
    }

    /* ── Navigation ──────────────────────────────────────── */

    goTo(next, opts) {
      var count = this.slides.length;
      // Wrap in both directions so prev from 0 lands on the last.
      var target = ((next % count) + count) % count;
      if (target === this.index) return;

      var prev = this.index;
      this.index = target;

      this.slides[prev].classList.remove('is-active');
      this.slides[prev].setAttribute('aria-hidden', 'true');

      this.slides[target].classList.add('is-active');
      this.slides[target].removeAttribute('aria-hidden');

      this._syncControls();

      // Only announce when the change was user-driven; autoplay
      // narration would be constant chatter.
      if (this.live && opts && opts.announce) {
        this.live.textContent = 'Slide ' + (target + 1) + ' of ' + count;
      }

      this._restart();
    }

    next() {
      this.goTo(this.index + 1);
    }

    prev() {
      this.goTo(this.index - 1);
    }

    _syncControls() {
      var i = this.index;

      this.dashes.forEach(function (dash, n) {
        var on = n === i;
        dash.setAttribute('aria-current', on ? 'true' : 'false');
        dash.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      this.thumbs.forEach(function (thumb, n) {
        var on = n === i;
        thumb.classList.toggle('is-active', on);
        thumb.setAttribute('aria-current', on ? 'true' : 'false');
      });

      if (this.counter) {
        this.counter.textContent = ('0' + (i + 1)).slice(-2);
      }
    }

    _bindControls() {
      var self = this;
      this.querySelectorAll('[data-goto]').forEach(function (el) {
        el.addEventListener('click', function () {
          self.goTo(parseInt(el.dataset.goto, 10), { announce: true });
        });
      });
    }

    _bindKeys() {
      var self = this;
      this.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          self.goTo(self.index + 1, { announce: true });
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          self.goTo(self.index - 1, { announce: true });
        }
      });
    }

    /* ── Touch ───────────────────────────────────────────── */

    _bindSwipe() {
      var self = this;
      var startX = null;
      var startY = null;

      var viewport = this.querySelector('.ideas-hero__viewport');
      if (!viewport) return;

      viewport.addEventListener(
        'touchstart',
        function (e) {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        },
        { passive: true }
      );

      viewport.addEventListener(
        'touchend',
        function (e) {
          if (startX === null) return;
          var dx = e.changedTouches[0].clientX - startX;
          var dy = e.changedTouches[0].clientY - startY;
          startX = startY = null;

          // Ignore anything that reads as a vertical scroll.
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;

          if (dx < 0) self.goTo(self.index + 1, { announce: true });
          else self.goTo(self.index - 1, { announce: true });
        },
        { passive: true }
      );
    }

    /* ── Autoplay ────────────────────────────────────────── */

    _start() {
      if (!this.wantsAutoplay || REDUCED.matches) return;
      this._stop();
      var self = this;
      this.timer = setInterval(function () {
        self.goTo(self.index + 1);
      }, this.interval);
    }

    _stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    // goTo() calls this so a manual change restarts the dwell rather
    // than advancing again a moment later.
    _restart() {
      if (!this.timer) return;
      this._start();
    }

    _bindPause() {
      var self = this;
      var pause = function () {
        self._stop();
      };
      var resume = function () {
        if (!document.hidden) self._start();
      };

      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', function (e) {
        // Only resume once focus has genuinely left the slider.
        if (!self.contains(e.relatedTarget)) resume();
      });

      this._onVisibility = function () {
        if (document.hidden) self._stop();
        else self._start();
      };
      document.addEventListener('visibilitychange', this._onVisibility);

      // A reduced-motion preference toggled mid-session should take
      // effect without a reload.
      var onPref = function () {
        if (REDUCED.matches) self._stop();
        else self._start();
      };
      if (REDUCED.addEventListener) REDUCED.addEventListener('change', onPref);
      else if (REDUCED.addListener) REDUCED.addListener(onPref);
    }
  }

  customElements.define('ideas-hero', IdeasHero);
})();
